import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import theme from '../../theme';

const Container = styled.span`
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--gray800);
    border-radius: 2px;
    padding: 6px 8px;
`;

const Arrow = styled.div.attrs((props) => ({ top: '0px', right: 'px0', ...props }))`
    position: absolute;
    top: ${(props) => props.top};
    right: ${(props) => props.right};
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-radius: 0 1px;

    border-bottom: 5px solid var(--gray800);
`;

Arrow.propTypes = {
    top: PropTypes.string,
    right: PropTypes.string,
};

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
`;

const TooltipWrapper = styled.div`
    transform: translateX(50%);
    right: 50%;

    z-index: 1;
    position: absolute;
    animation: ${fadeIn} 100ms ease-in;
`;

const Tooltip = ({ content, className, ...other }) => {
    const wrapperRef = useRef();
    const arrowRef = useRef();
    const containerRef = useRef();

    useEffect(() => {
        const wrapper = wrapperRef.current;

        const onAnimationStart = () => {
            const container = containerRef.current;
            const arrow = arrowRef.current;

            container.style.marginLeft = 0;
            arrow.style.right = 0;

            const width = document.body.clientWidth;
            const l = wrapper.getBoundingClientRect().left;
            const r = wrapper.getBoundingClientRect().right;
            const w = wrapper.getBoundingClientRect().width;

            const arrowRight = Math.round(w / 2) - 6;
            arrow.style.right = `${arrowRight}px`;

            if (l < 0) {
                const padding = width > theme.breakpoints.tablet ? 24 : 16;

                container.style.marginLeft = `${(Math.abs(l) + padding) * 2}px`;
                arrow.style.right = `${Math.abs(l - arrowRight - padding)}px`;
            }
            if (r > width) {
                const diff = Math.round(width - r - 5);
                const marginLeft = `${diff * 2}px`;
                container.style.marginLeft = marginLeft;

                arrow.style.right = `${arrowRight + diff}px`;
            }
        };

        wrapper.addEventListener('webkitAnimationStart', onAnimationStart);
        wrapper.addEventListener('mozAnimationStart', onAnimationStart);
        wrapper.addEventListener('animationStart', onAnimationStart);

        return () => {
            wrapper.removeEventListener('webkitAnimationStart', onAnimationStart);
            wrapper.removeEventListener('mozAnimationStart', onAnimationStart);
            wrapper.removeEventListener('animationStart', onAnimationStart);
        };
    }, []);

    return (
        <TooltipWrapper ref={wrapperRef} className="TooltipWrapper">
            <Arrow ref={arrowRef} {...other} />
            <Container ref={containerRef} className={className}>
                {content}
            </Container>
        </TooltipWrapper>
    );
};

Tooltip.propTypes = {
    content: PropTypes.string,
    className: PropTypes.string,
};

export default Tooltip;
